import { useEffect, useRef, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import './DicomViewer.css';
import cornerstone from 'cornerstone-core';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import DicomTags from './DicomTags';
import WindowAdjustment from './WindowAdjustment';
import ImageNavigationControls from './ImageNavigationControls';
import CornerstoneViewport from './CornerstoneViewport';
import { AuthContext } from '../App';

const findBoundary = (data, boundary, start) => {
  for (let i = start; i < data.length - boundary.length; i++) {
    let match = true;
    for (let j = 0; j < boundary.length; j++) {
      if (data[i + j] !== boundary[j]) {
        match = false;
        break;
      }
    }
    if (match) return i;
  }
  return -1;
};

const findHeaderEnd = (data, start) => {
  for (let i = start; i < data.length - 3; i++) {
    if (data[i] === 13 && data[i + 1] === 10 && 
        data[i + 2] === 13 && data[i + 3] === 10) {
      return i + 4;
    }
  }
  return -1;
};

const parseMultipartResponse = (arrayBuffer, boundary) => {
  const uint8Array = new Uint8Array(arrayBuffer);
  const parts = [];
  const boundaryBytes = new TextEncoder().encode(`--${boundary}`);
  
  let start = 0;
  let pos = 0;

  while (pos < uint8Array.length) {
    const boundaryPos = findBoundary(uint8Array, boundaryBytes, pos);
    if (boundaryPos === -1) break;

    if (start > 0) {
      const headerEnd = findHeaderEnd(uint8Array, start);
      if (headerEnd !== -1) {
        const dataStart = headerEnd;
        const dataEnd = boundaryPos - 2;
        if (dataStart < dataEnd) {
          const dicomData = uint8Array.slice(dataStart, dataEnd);
          parts.push(dicomData);
        }
      }
    }

    start = boundaryPos + boundaryBytes.length + 2;
    pos = start;
  }

  return parts;
};

const DicomSeriesViewer = () => {
  const { seriesUid } = useParams();
  const { apiBaseUrl, token } = useContext(AuthContext);
  const viewportRef = useRef(null);
  const [imageIds, setImageIds] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const imageIdsRef = useRef([]);
  const indexRef = useRef(0);
  const wheelTimeoutRef = useRef(null);
  const [windowCenter, setWindowCenter] = useState(40);
  const [windowWidth, setWindowWidth] = useState(400);
  const [dicomMetadata, setDicomMetadata] = useState(null);
  const [windowPresets, setWindowPresets] = useState([]);

  useEffect(() => {
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    cornerstoneWADOImageLoader.configure({ useWebWorkers: false });

    return () => {
      imageIdsRef.current.forEach(id => {
        if (typeof id === 'string' && id.startsWith('wadouri:blob:')) {
          const blobUrl = id.replace(/^wadouri:/, '');
          try { URL.revokeObjectURL(blobUrl); } catch (e) {}
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!seriesUid || !token) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const fetchSeries = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${apiBaseUrl}/wado/series/${seriesUid}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch series: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('Content-Type');

        if (!contentType || !contentType.includes('multipart/related')) {
          throw new Error(`Invalid response content type: ${contentType}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const boundary = contentType.split('boundary=')[1];
        const parts = parseMultipartResponse(arrayBuffer, boundary);

        const ids = parts.map(part => {
          const blob = new Blob([part], { type: 'application/dicom' });
          return 'wadouri:' + URL.createObjectURL(blob);
        });

        setImageIds(ids);
        imageIdsRef.current = ids;
        setIndex(0);
        indexRef.current = 0;
        setLoading(false);
        setTimeout(() => loadImageAt(0), 50);

      } catch (err) {
        const msg = err.name === 'AbortError'
          ? 'Request timed out after 30 seconds'
          : err.message || 'Unknown fetch error';
        setError(msg);
        setLoading(false);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    fetchSeries();
    return () => { controller.abort(); clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesUid, apiBaseUrl, token]);

  const enableViewport = () => {
    const el = viewportRef.current;
    if (!el) return null;
    try { cornerstone.enable(el); } catch (e) {}
    return el;
  };

  const loadImageAt = (i) => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    if (i < 0 || i >= ids.length) return;
    const id = ids[i];
    const el = enableViewport();
    if (!el) return;

    cornerstone.loadAndCacheImage(id).then(image => {
      try {
        cornerstone.displayImage(el, image);
        cornerstone.resize(el);
        cornerstone.fitToWindow(el);

        const viewport = cornerstone.getViewport(el);
        const wc = Math.round(viewport.voi.windowCenter);
        const ww = Math.round(viewport.voi.windowWidth);
        setWindowCenter(wc);
        setWindowWidth(ww);

        if (image.data) {
          const metadata = {
            rows: image.rows,
            columns: image.columns,
            windowCenter: wc,
            windowWidth: ww,
            rescaleSlope: image.slope || 1,
            rescaleIntercept: image.intercept || 0
          };
          setDicomMetadata(metadata);

          try {
            const dataSet = image.data;
            if (dataSet && dataSet.string) {
              const wcStr = dataSet.string('x00281050');
              const wwStr = dataSet.string('x00281051');
              const descStr = dataSet.string('x00281055');

              if (wcStr && wwStr) {
                const centers = wcStr.split('\\').map(v => parseFloat(v));
                const widths = wwStr.split('\\').map(v => parseFloat(v));
                const descriptions = descStr ? descStr.split('\\') : [];

                const presets = [];
                for (let p = 0; p < Math.min(centers.length, widths.length); p++) {
                  presets.push({
                    name: descriptions[p] || `Preset ${p + 1}`,
                    center: centers[p],
                    width: widths[p]
                  });
                }
                setWindowPresets(presets);
              }
            }
          } catch { }
        }
      } catch { }
    }).catch(() => { });
  };

  const onPrev = () => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    const ni = Math.max(0, indexRef.current - 1);
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
  };

  const onNext = () => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    const ni = Math.min(ids.length - 1, indexRef.current + 1);
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
  };

  const handleWheel = (e) => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    if (wheelTimeoutRef.current) return;
    const delta = e.deltaY;
    let ni = indexRef.current + (delta > 0 ? 1 : -1);
    ni = Math.max(0, Math.min(ids.length - 1, ni));
    if (ni === indexRef.current) return;
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
    wheelTimeoutRef.current = setTimeout(() => { wheelTimeoutRef.current = null; }, 80);
  };

  const onWindowCenterChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setWindowCenter(value);
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowCenter = value;
        cornerstone.setViewport(el, viewport);
      } catch (e) {}
    }
  };

  const onWindowWidthChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setWindowWidth(value);
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowWidth = value;
        cornerstone.setViewport(el, viewport);
      } catch (e) {}
    }
  };

  const resetWindowLevel = () => {
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        cornerstone.reset(el);
        const newViewport = cornerstone.getViewport(el);
        setWindowCenter(Math.round(newViewport.voi.windowCenter));
        setWindowWidth(Math.round(newViewport.voi.windowWidth));
      } catch (e) {}
    }
  };

  const applyPreset = (preset) => {
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowCenter = preset.center;
        viewport.voi.windowWidth = preset.width;
        cornerstone.setViewport(el, viewport);
        setWindowCenter(Math.round(preset.center));
        setWindowWidth(Math.round(preset.width));
      } catch (e) {}
    }
  };

  if (loading) {
    return (
      <div className="dicom-viewer">
        <h2>Loading Series via WADO...</h2>
        <p>Fetching DICOM images from server...</p>
        <p style={{fontSize: '0.8em', color: '#888'}}>Series: {seriesUid || '(none)'}</p>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dicom-viewer">
        <h2>Error Loading Series</h2>
        <p className="error">{error}</p>
        <p>Series UID: {seriesUid}</p>
      </div>
    );
  }

  return (
    <div className="dicom-viewer">
      <h2>DICOM Series Viewer (WADO)</h2>
      <p>Series UID: {seriesUid}</p>
      <p>Retrieved {imageIds.length} images from server</p>
      <br/>

      <ImageNavigationControls
        index={index}
        totalImages={imageIds.length}
        onPrev={onPrev}
        onNext={onNext}
      />

      <WindowAdjustment
        windowCenter={windowCenter}
        windowWidth={windowWidth}
        onWindowCenterChange={onWindowCenterChange}
        onWindowWidthChange={onWindowWidthChange}
        onReset={resetWindowLevel}
        windowPresets={windowPresets}
        onApplyPreset={applyPreset}
        disabled={imageIds.length === 0}
      />

      <CornerstoneViewport
        ref={viewportRef}
        width="100%"
        height="512px"
        onWheel={handleWheel}
      />

      <DicomTags
        metadata={dicomMetadata}
        windowCenter={windowCenter}
        windowWidth={windowWidth}
        windowPresets={windowPresets}
      />
    </div>
  );
};

export default DicomSeriesViewer;
